/**
 * socialStatusLabels — the ONE German vocabulary for what a post or a channel is
 * doing right now, plus the semantic TONE each state carries.
 *
 * Two levels, deliberately kept apart because they answer different questions:
 *   POST level   — the row in the dashboard list ("Wartet auf Freigabe").
 *   CHANNEL level — one destination inside a post ("Wird gesendet").
 * The channel side is keyed by a DERIVED state (see
 * AdminSocialReviewerPage/reviewerChannelState), not by the raw `status` string:
 * a channel resting at `pending` inside a post that is `publishing` is in flight,
 * and a channel carrying `heldForManual` is waiting for a human — neither reads
 * off `status` alone.
 *
 * The dashboard pill, the reviewer's platform dots, its channel-state row and its
 * done-strip used to phrase the same three states three different ways
 * ("veröffentlicht" / "Veröffentlicht" / "Published"), and none of them covered a
 * channel mid-send. Every surface now reads its words from here.
 *
 * TONE is a semantic key ('ok' | 'error' | 'warn' | 'info' | 'neutral'), never a
 * CSS class: the dashboard and the reviewer own different stylesheets and map the
 * tone to their own tokens.
 *
 * ZERO imports (client-safe ESM, webpack-4 bundled). NO ES2020 logical-assignment
 * (`||=`/`&&=`/`??=`) — CI-guarded by sourceLogicalAssignment.test.js.
 *
 * @module AdminSocialPostsPage/socialStatusLabels
 */

// ---- Post level ----

const POST_STATUS_LABELS = {
    queued: 'In Warteschlange',
    generating: 'Wird erstellt',
    pending_approval: 'Wartet auf Freigabe',
    approved: 'Freigegeben',
    publishing: 'Wird gesendet',
    published: 'Veröffentlicht',
    partially_failed: 'Teilweise fehlgeschlagen',
    failed: 'Fehlgeschlagen',
    cancelled: 'Abgebrochen',
    awaiting_manual: 'Wartet auf manuelles Posten',
};

const POST_STATUS_TONES = {
    queued: 'info',
    generating: 'info',
    pending_approval: 'warn',
    approved: 'info',
    publishing: 'info',
    published: 'ok',
    partially_failed: 'error',
    failed: 'error',
    cancelled: 'neutral',
    awaiting_manual: 'warn',
};

/**
 * The German name of a post status; the raw key (underscores spaced) when unknown,
 * so a new status is visible rather than blank.
 * @param {string} status
 * @returns {string}
 */
const postStatusLabel = status => POST_STATUS_LABELS[status] || (status || '').replace(/_/g, ' ');

/**
 * The semantic tone of a post status. Unknown statuses read as informational
 * rather than as a failure.
 * @param {string} status
 * @returns {'ok'|'error'|'warn'|'info'|'neutral'}
 */
const postStatusTone = status => POST_STATUS_TONES[status] || 'info';

/**
 * What the post's own pill should say. Two channel-level outcomes outrank the post
 * status because they are what the operator is actually waiting on: a Postiz
 * acceptance still awaiting the platform's confirmation, and a Postiz draft (which
 * is "published" from the app's side but is not live anywhere).
 * @param {{status?:string, channels?:Array}} post
 * @returns {string}
 */
const postStatusLabelForPost = post => {
    const channels = (post && post.channels) || [];
    if (channels.some(c => c && c.status === 'accepted' && c.postizOutcome === 'accepted')) {
        return 'Wartet auf Plattform-Bestätigung';
    }
    if (channels.some(c => c && c.postizOutcome === 'draft')) return 'Postiz-Entwurf erstellt';
    return postStatusLabel(post && post.status);
};

// ---- Channel level (derived states) ----

// The full set of states one destination can be in, in the order the reviewer
// reasons about them. `ready` is the only state with no pill: nothing has happened
// yet, and the publish button already says what will.
const CHANNEL_STATE_COPY = {
    ready: { label: 'Bereit zum Senden', tone: 'neutral' },
    publishing: { label: 'Wird gesendet', tone: 'info' },
    stalled: {
        label: 'Wird gesendet',
        // The server's reclaim cron rolls a stuck row back out of `publishing`
        // by itself, so the honest message is that it self-heals — there is
        // nothing for the operator to press, and a "force" button would only
        // invite a duplicate post.
        detail: 'hängt fest und wird automatisch freigegeben',
        tone: 'warn',
    },
    accepted: {
        label: 'Von Postiz angenommen',
        detail: 'wird bestätigt',
        tone: 'info',
    },
    awaiting_manual: { label: 'Wartet auf manuelles Posten', tone: 'warn' },
    released_manual: { label: 'An die Browser-Erweiterung übergeben', tone: 'neutral' },
    published: { label: 'Veröffentlicht', tone: 'ok' },
    draft_created: { label: 'Postiz-Entwurf erstellt', tone: 'neutral' },
    skipped: { label: 'Übersprungen', tone: 'neutral' },
    failed: { label: 'Fehlgeschlagen', tone: 'error' },
};

/**
 * The copy + tone for a derived channel state.
 * @param {string} key one of the CHANNEL_STATE_COPY keys
 * @returns {{label:string, detail?:string, tone:string}}
 */
const channelStateCopy = key => CHANNEL_STATE_COPY[key] || { label: key || '', tone: 'neutral' };

// ---- Blocked actions ----

// Why the delete button is off. Both statuses mean a server process owns the row
// right now; deleting underneath it would race the publish spine.
const DELETE_BLOCKED_REASONS = {
    generating: 'Wird gerade erstellt — Löschen ist so lange gesperrt.',
    publishing: 'Wird gerade veröffentlicht — Löschen ist so lange gesperrt.',
};

/**
 * The reason deleting this post is blocked, or null when it is allowed. Callers
 * derive both the disabled state and its tooltip from this single answer.
 * @param {string} status
 * @returns {string|null}
 */
const deleteBlockedReason = status => DELETE_BLOCKED_REASONS[status] || null;

export {
    postStatusLabel,
    postStatusTone,
    postStatusLabelForPost,
    channelStateCopy,
    deleteBlockedReason,
};
